/**
 * Roster de NPCs/hotspots de Saffron (PLANO-DIFERENCIAIS Pilar 3 — Cidade Viva).
 * Cada entrada é só dado: posição percentual no mapa (mesma escala de `cityPlayer.x/y`
 * em `app.js`), painel a abrir e raio de interação. Sem lógica de render/DOM aqui —
 * a UI decide como desenhar o sprite. `colors` (head/body/legs/feet) reusa o tint
 * OTClient de `outfit-colors.js` nos looks `look-*`.
 */

/** `x`/`y` em porcentagem do mundo da cidade (mesma escala de `cityPlayer`). */
const CITY_NPC_ROSTER = Object.freeze([
  Object.freeze({
    id: 'nurse', panel: 'center', labelKey: 'npc.nurse', x: 47.16, y: 39.49, radius: 6,
    teleportX: 47.87, teleportY: 40.8, teleportDir: 'north', sprite: 'nurse-joy',
  }),
  Object.freeze({
    id: 'shop', panel: 'shop', labelKey: 'npc.shop', x: 31.9, y: 57.25, radius: 6,
    teleportX: 31.56, teleportY: 55.43, teleportDir: 'south', sprite: 'pokemon-collector',
  }),
  Object.freeze({
    id: 'tmShop', panel: 'tm-shop', labelKey: 'npc.tmShop', x: 33.7, y: 56.2, radius: 4,
    teleportX: 34.4, teleportY: 56.88, teleportDir: 'west', sprite: 'shopkeeper-mark',
  }),
  Object.freeze({
    id: 'fisher', panel: 'fish', labelKey: 'npc.fisher', x: 46.8, y: 54, radius: 5,
    teleportX: 46.8, teleportY: 55.5, teleportDir: 'north', sprite: 'look-fisher-m',
    // azul marinho / amarelo boia
    colors: Object.freeze({ head: 94, body: 83, legs: 48, feet: 75 }),
  }),
  Object.freeze({
    id: 'professor', panel: 'professor', labelKey: 'npc.professor', x: 39, y: 36, radius: 4,
    teleportX: 39, teleportY: 37.4, teleportDir: 'north', sprite: 'look-veteran-m',
    // bege / marrom academia
    colors: Object.freeze({ head: 90, body: 40, legs: 92, feet: 108 }),
  }),
  Object.freeze({
    id: 'auctioneer', panel: 'market', labelKey: 'npc.auctioneer', x: 61, y: 36, radius: 4,
    teleportX: 61, teleportY: 37.4, teleportDir: 'north', sprite: 'look-business-m',
    // verde-escuro / dourado
    colors: Object.freeze({ head: 108, body: 79, legs: 54, feet: 75 }),
  }),
  Object.freeze({
    id: 'apothecary', panel: 'cook', labelKey: 'npc.apothecary', x: 39, y: 43, radius: 4,
    teleportX: 39, teleportY: 44.4, teleportDir: 'north', sprite: 'look-psychic-f',
    // lilás / teal
    colors: Object.freeze({ head: 87, body: 86, legs: 46, feet: 0 }),
  }),
  Object.freeze({
    id: 'blacksmith', panel: 'craft', labelKey: 'npc.blacksmith', x: 61, y: 43, radius: 4,
    teleportX: 61, teleportY: 44.4, teleportDir: 'north', sprite: 'look-hiker-m',
    // carvão / ferrugem / cinza forja
    colors: Object.freeze({ head: 91, body: 108, legs: 72, feet: 126 }),
  }),
  Object.freeze({
    id: 'miner', panel: 'mine', labelKey: 'npc.miner', x: 39.8, y: 60, radius: 4,
    teleportX: 39.8, teleportY: 61.4, teleportDir: 'north', sprite: 'look-hiker-f',
    // terra / âmbar / botas escuras
    colors: Object.freeze({ head: 94, body: 38, legs: 56, feet: 108 }),
  }),
  Object.freeze({
    id: 'clanHerald', panel: 'clan', labelKey: 'npc.clanHerald', x: 43.5, y: 60.5, radius: 4,
    teleportX: 42.2, teleportY: 60.5, teleportDir: 'east', sprite: 'look-oriental-m',
    // vermelho / dourado / preto heráldica
    colors: Object.freeze({ head: 75, body: 73, legs: 126, feet: 108 }),
  }),
  Object.freeze({
    id: 'questBoard', panel: 'quests', labelKey: 'npc.questBoard', x: 47.8, y: 59.5, radius: 4,
    teleportX: 47.8, teleportY: 60.9, teleportDir: 'north', sprite: 'look-adventurer-f',
    // verde floresta / couro
    colors: Object.freeze({ head: 94, body: 78, legs: 56, feet: 108 }),
  }),
  Object.freeze({
    id: 'gymLeader', panel: 'gym', labelKey: 'npc.gymLeader', x: 53.5, y: 61, radius: 4,
    teleportX: 53.5, teleportY: 62.4, teleportDir: 'north', sprite: 'look-atletic-m',
    // azul esportivo / branco
    colors: Object.freeze({ head: 0, body: 84, legs: 0, feet: 72 }),
  }),
]);

// Only locations with an authored building/door remain on the map. Craft and
// apothecary are exposed as hub cards; gated/duplicate NPCs are not teleport
// destinations and cannot open dead panels.
export const CITY_NPCS = Object.freeze(CITY_NPC_ROSTER.slice(0, 4));

export const CITY_NPC_BY_ID = Object.freeze(
  Object.fromEntries(CITY_NPCS.map((npc) => [npc.id, npc])),
);

/** Distância euclidiana simples no espaço percentual do mapa. */
export function distanceToNpc(npc, x, y) {
  return Math.hypot((Number(x) || 0) - npc.x, (Number(y) || 0) - npc.y);
}

export function isPlayerNearNpc(npc, x, y) {
  return !!npc && distanceToNpc(npc, x, y) <= npc.radius;
}

/** NPC mais próximo dentro do raio de interação (ou `null`). */
export function npcNear(x, y, npcs = CITY_NPCS) {
  let closest = null;
  let closestDistance = Infinity;
  for (const npc of npcs) {
    const distance = distanceToNpc(npc, x, y);
    if (distance <= npc.radius && distance < closestDistance) {
      closest = npc;
      closestDistance = distance;
    }
  }
  return closest;
}
